/**
 * Pure helper functions for DataTable.
 * Handles column width config, row action resolution, injected column value reading,
 * and miscellaneous utilities that carry no React state.
 */
import * as React from 'react'
import type { RowActionItem } from '../RowActions'

export const DEFAULT_ROW_CLICK_ACTION_IDS = ['edit', 'open']

export function withDataTableNamespaces<T extends Record<string, unknown>>(
  mappedRow: T,
  sourceItem: Record<string, unknown>,
): T & Record<string, unknown> {
  const namespaced: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(sourceItem)) {
    if (!key.startsWith('_')) continue
    namespaced[key] = value
  }
  return {
    ...mappedRow,
    ...namespaced,
  }
}

export function resolveDefaultRowAction(items: RowActionItem[], preferredIds: string[]): RowActionItem | null {
  for (const preferredId of preferredIds) {
    const match = items.find((item) => item.id === preferredId && (item.href || item.onSelect))
    if (match) return match
  }
  for (const preferredId of preferredIds) {
    const match = items.find((item) => item.label.toLowerCase() === preferredId && (item.href || item.onSelect))
    if (match) return match
  }
  return null
}

export function pickDefaultRowAction(node: React.ReactNode, preferredIds: string[]): RowActionItem | null {
  if (!React.isValidElement(node)) return null
  const items = (node.props as { items?: RowActionItem[] }).items
  if (!Array.isArray(items)) return null
  return resolveDefaultRowAction(items, preferredIds)
}

type ColumnTruncateConfig = {
  maxWidth: string
  truncate: boolean
}

type ColumnTruncateMeta = {
  truncate?: boolean
  maxWidth?: string
}

export function getColumnTruncateConfig(columnId: string, accessorKey?: string, columnMeta?: ColumnTruncateMeta): ColumnTruncateConfig {
  const key = accessorKey || columnId
  const metaMaxWidth = typeof columnMeta?.maxWidth === 'string' ? columnMeta.maxWidth.trim() : ''

  if (key.startsWith('cf_') || key.startsWith('cf:')) {
    return {
      maxWidth: metaMaxWidth || '120px',
      truncate: typeof columnMeta?.truncate === 'boolean' ? columnMeta.truncate : true,
    }
  }

  const wideColumns = ['title', 'name', 'description', 'source', 'companies', 'people']
  if (wideColumns.includes(key)) {
    return {
      maxWidth: metaMaxWidth || '250px',
      truncate: typeof columnMeta?.truncate === 'boolean' ? columnMeta.truncate : true,
    }
  }

  const mediumColumns = ['status', 'pipelineStage', 'pipeline_stage', 'type', 'category']
  if (mediumColumns.includes(key)) {
    return {
      maxWidth: metaMaxWidth || '180px',
      truncate: typeof columnMeta?.truncate === 'boolean' ? columnMeta.truncate : true,
    }
  }

  if (key.endsWith('_at') || key.endsWith('At') || key.includes('date') || key.includes('Date')) {
    return {
      maxWidth: metaMaxWidth || '120px',
      truncate: typeof columnMeta?.truncate === 'boolean' ? columnMeta.truncate : true,
    }
  }

  return {
    maxWidth: metaMaxWidth || '150px',
    truncate: typeof columnMeta?.truncate === 'boolean' ? columnMeta.truncate : true,
  }
}

export function readInjectedColumnValue(row: unknown, accessorKey: string): unknown {
  if (!row || typeof row !== 'object' || !accessorKey) return undefined
  const segments = accessorKey.split('.').filter((segment) => segment.length > 0)
  if (segments.length === 0) return undefined
  let current: unknown = row
  for (const segment of segments) {
    if (!current || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return current
}

export function shouldSkipTruncation(columnId: string): boolean {
  const skipColumns = ['actions', 'select', 'checkbox', 'expand']
  return skipColumns.includes(columnId.toLowerCase())
}

export function normalizeLabel(input: string): string {
  if (!input) return ''
  return input
    .replace(/^cf[_:]/, '')
    .replace(/[_:\-]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function collectUniqueById<T extends { id: string }>(
  entries: T[],
  warningScope: string,
): T[] {
  const byId = new Map<string, T>()
  for (const entry of entries) {
    if (!entry.id) continue
    if (byId.has(entry.id)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[UMES] Duplicate injected ${warningScope} id "${entry.id}" detected. Keeping the first entry.`)
      }
      continue
    }
    byId.set(entry.id, entry)
  }
  return Array.from(byId.values())
}
